import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Paper,
  TextField,
  Alert,
  IconButton,
  Link,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import { format, parse, addMinutes, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { canStaffManuallyMarkAppointmentPaid, getStatusColor } from '../../utils/appointmentUtils';
import { getAppointmentDisplayMeta } from '../../utils/appointmentWorkflow';
import {
  Description as DocumentIcon,
  AccessTime as TimeIcon,
  Event as EventIcon,
  Person as PersonIcon,
  LocalOffer as ServiceIcon,
  AttachMoney as PaymentIcon,
  Add as AddOnIcon,
  Inventory as PackageIcon,
  Notes as NotesIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Info as InfoIcon,
  LocationOn as LocationOnIcon,
  History as HistoryIcon,
  Cancel,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import RepeatIcon from '@mui/icons-material/Repeat';
import QuickActions from './QuickActions';
import { useAppointmentUpdates } from '../../hooks/useAppointmentUpdates';
import { getClient } from '../../api/companyRecords';
import { listAppointments, upsertAppointment } from '../../api/appointments';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AppointmentMedia from './media/AppointmentMedia';
import { alpha } from '@mui/material/styles';
import GradientButton from '../buttons/GradientButton';
import { buildDirectionsUrl, hasRoutableAddress, formatAddress } from '../../features/directions/directionsUtils';
import AppointmentDetailsPageLayout from './AppointmentDetailsPageLayout';
import AppointmentSmsChatPopout from './appointment-sms-chat/AppointmentSmsChatPopout';
import { usePermissions } from '../../company-management/hooks/usePermissions';
import { PERMISSIONS } from '../../company-management/constants/permissions';
import { archiveCompanyAppointment } from '../../shared/booking/appointments/archiveCompanyAppointment';
import { useNotification } from '../../context/NotificationContext';

function nextAppointmentMediaList(appointment, type, file, action) {
  const key = `${type}s`;
  const current = Array.isArray(appointment?.[key]) ? appointment[key] : [];
  if (action === 'add') return [...current, file];
  return current.filter((item) => {
    if (!file) return true;
    if (item === file) return false;
    if (item?.url && file?.url && item.url === file.url) return false;
    if (item?.id && file?.id && item.id === file.id) return false;
    if (item?.path && file?.path && item.path === file.path) return false;
    return true;
  });
}

function toAppointmentDate(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (value?.toDate && typeof value.toDate === 'function') return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const AppointmentDetailsDialog = ({
  appointment,
  open,
  onClose,
  onUpdate,
  onLiveAppointmentWillBeDeleted,
  variant = 'dialog',
  isArchived = false
}) => {
  const isPage = variant === 'page';
  const showDialog = open || isPage;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { companyData, user } = useAuth();
  const { showToast } = useNotification();
  const { hasPermission } = usePermissions();
  const canClientSms = hasPermission(PERMISSIONS.CLIENTS.VIEW);
  const [clientDetails, setClientDetails] = useState(null);
  const [clientProfileLoading, setClientProfileLoading] = useState(false);
  const [localAppointment, setLocalAppointment] = useState(appointment);
  const appointmentData = localAppointment || {};
  const [confirmCancelDialog, setConfirmCancelDialog] = useState({ open: false, cancelAll: false });
  const [cancellingAppointments, setCancellingAppointments] = useState(false);
  const { addUpdate } = useAppointmentUpdates(companyData?.id);
  const recordStaffUpdate = (...args) => {
    void addUpdate(...args).catch((error) => {
      console.warn('Non-blocking: failed to write updates record', error);
    });
  };
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [isRescheduleHistoryExpanded, setIsRescheduleHistoryExpanded] = useState(false);
  // Compact button styling to keep actions unobtrusive in this dialog
  const compactButtonStyle = {
    paddingBlock: isMobile ? '0.2rem' : '0.35rem',
    paddingInline: isMobile ? '0.5rem' : '0.75rem',
    fontSize: isMobile ? '0.75rem' : '0.82rem',
    borderRadius: 10
  };


  // Update local appointment when prop changes
  useEffect(() => {
    setLocalAppointment(appointment);
    setIsRescheduleHistoryExpanded(false);
  }, [appointment]);

  // Fetch client details when appointment changes
  useEffect(() => {
    if (!appointment?.clientId || !companyData?.id) {
      setClientDetails(null);
      setClientProfileLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setClientProfileLoading(true);
      try {
        const clientData = await getClient({
          companyId: companyData.id,
          id: appointment.clientId,
        });

        if (cancelled) return;

        if (clientData) {
          setClientDetails(clientData);
        } else {
          setClientDetails(null);
        }
      } catch (error) {
        console.error('Error fetching client details:', error);
        if (!cancelled) setClientDetails(null);
      } finally {
        if (!cancelled) setClientProfileLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [appointment?.clientId, companyData?.id]);

  const appointmentSmsPhone = useMemo(
    () =>
      [appointmentData.clientPhone, clientDetails?.phone, appointmentData.customerInfo?.phone]
        .map((p) => (typeof p === 'string' ? p.trim() : ''))
        .find(Boolean) || '',
    [appointmentData.clientPhone, clientDetails?.phone, appointmentData.customerInfo?.phone]
  );

  const smsClientDisplayName = useMemo(() => {
    const fromAppointment = (appointmentData.clientName || '').trim();
    if (fromAppointment) return fromAppointment;
    const fromClient = `${clientDetails?.firstName || ''} ${clientDetails?.lastName || ''}`.trim();
    return fromClient || undefined;
  }, [appointmentData.clientName, clientDetails?.firstName, clientDetails?.lastName]);

  const handleUpdate = () => {
    if (onUpdate) {
      onUpdate();
    }
  };

  const getCurrentUpdaterMetadata = () => {
    const firstName = user?.firstName?.trim?.() || '';
    const lastName = user?.lastName?.trim?.() || '';
    const combinedName = `${firstName} ${lastName}`.trim();
    const fallbackName =
      user?.displayName ||
      user?.name ||
      user?.email?.split('@')?.[0] ||
      '';

    return {
      updatedByName: combinedName || fallbackName || undefined,
      updatedByUid: user?.uid || undefined
    };
  };

  const calculateTotalDuration = (appointment) => {
    // Start with base duration
    let totalDuration = 0;

    // If it's a package appointment, use package duration
    if (appointment.selectedPackage?.duration || appointment.package?.duration) {
      totalDuration = parseInt(appointment.selectedPackage?.duration || appointment.package?.duration);
    } else {
      // Otherwise use service duration
      totalDuration = parseInt(appointment.serviceDuration || appointment.service?.duration || appointment.duration || 0);
    }

    // Add buffer time if exists
    const bufferTime = parseInt(
      appointment.selectedPackage?.bufferTime || 
      appointment.package?.bufferTime || 
      appointment.bufferTime || 
      0
    );

    // Add durations from add-ons
    const addOns = appointment.addOns || 
                  appointment.selectedAddons || 
                  appointment.selectedAddOns || 
                  appointment.service?.addOns || 
                  [];

    const addOnsDuration = addOns.reduce((total, addon) => {
      return total + (parseInt(addon.duration || 0) + parseInt(addon.bufferTime || 0));
    }, 0);

    return totalDuration + bufferTime + addOnsDuration;
  };

  // Update the getServiceDetails function
  const getServiceDetails = (appointment) => {
    const addOns = appointment.addOns ||
                  appointment.selectedAddons ||
                  appointment.selectedAddOns ||
                  appointment.service?.addOns ||
                  [];

    const packageDetails = appointment.package ||
                         appointment.selectedPackage ||
                         appointment.service?.package ||
                         null;

    const totalDuration = calculateTotalDuration(appointment);

    // Package "addons that come with it": includedServices (client-portal), selectedAddons (packages-v2), or appointment.includedAddons
    const packageIncludedServices = packageDetails?.includedServices ?? appointment.includedServices ?? [];
    const packageSelectedAddons = packageDetails?.selectedAddons ?? [];
    const appointmentIncludedAddons = appointment.includedAddons ?? [];
    const mapToIncludedShape = (a) => ({
      serviceName: a.name || a.serviceName,
      name: a.name || a.serviceName,
      duration: a.duration != null ? a.duration : 0,
      price: a.price != null ? a.price : 0,
      sessions: a.sessions != null ? a.sessions : 1
    });
    const fromPackageAddons = packageSelectedAddons.map(mapToIncludedShape);
    const fromAppointmentAddons = Array.isArray(appointmentIncludedAddons) ? appointmentIncludedAddons.map(mapToIncludedShape) : [];
    const includedServices = Array.isArray(packageIncludedServices) && packageIncludedServices.length > 0
      ? packageIncludedServices
      : (fromPackageAddons.length > 0 ? fromPackageAddons : fromAppointmentAddons);

    return {
      name: appointment.serviceName || appointment.service?.name || 'No service specified',
      duration: totalDuration,
      price: appointment.servicePrice || appointment.service?.price || 0,
      addOns: addOns,
      package: packageDetails ? {
        ...packageDetails,
        duration: parseInt(packageDetails.duration || 0),
        bufferTime: parseInt(packageDetails.bufferTime || 0),
        includedServices
      } : null
    };
  };

  // Update the ServiceDetails component to show package duration
  const ServiceDetails = ({ service }) => (
    <List dense disablePadding>
      {/* Main Service */}
      <ListItem>
        <ListItemIcon>
          {service.package ? <PackageIcon fontSize="small" color="primary" /> : <ServiceIcon fontSize="small" color="primary" />}
        </ListItemIcon>
        <ListItemText 
          primary={service.name}
          secondary={
            <>
              <Typography variant="caption" display="block" color="text.secondary">
                Price: ${service.price}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                Duration: {service.duration} min
                {service.package?.bufferTime > 0 && ` (+${service.package.bufferTime} min buffer)`}
              </Typography>
            </>
          }
          primaryTypographyProps={{ variant: 'body2', component: 'div' }}
          secondaryTypographyProps={{ component: 'div' }}
        />
      </ListItem>

      {/* Package Details if exists */}
      {service.package && (
        <ListItem sx={{ pl: 4 }}>
          <ListItemIcon>
            <AccessTimeIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary={`Package Duration: ${service.package.duration} min`}
            secondary={service.package.bufferTime > 0 ? `Buffer Time: ${service.package.bufferTime} min` : null}
            primaryTypographyProps={{ variant: 'body2' }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </ListItem>
      )}

      {/* Add-ons */}
      {service.addOns?.length > 0 && (
        <>
          <Typography variant="caption" sx={{ pl: 2, mt: 1, color: 'text.secondary' }}>
            Add-ons:
          </Typography>
          {service.addOns.map((addon, index) => (
            <ListItem key={index} sx={{ pl: 4 }}>
              <ListItemIcon>
                <AddOnIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary={addon.name}
                secondary={
                  <>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Duration: {addon.duration} min
                      {addon.bufferTime > 0 && ` (+${addon.bufferTime} min buffer)`}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Price: ${addon.price}
                    </Typography>
                  </>
                }
                primaryTypographyProps={{ variant: 'body2', component: 'div' }}
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItem>
          ))}
        </>
      )}
    </List>
  );

  // Add safe date formatting
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Not available';
    try {
      // Handle both Date objects and Firestore Timestamps
      const date = timestamp instanceof Date 
        ? timestamp 
        : timestamp.toDate 
          ? timestamp.toDate() 
          : new Date(timestamp);
      
      return format(date, 'PPp');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return null;
    try {
      const date = startTime instanceof Date 
        ? startTime 
        : startTime.toDate 
          ? startTime.toDate() 
          : new Date(startTime);
      
      return addMinutes(date, parseInt(duration));
    } catch (error) {
      console.error('Error calculating end time:', error);
      return null;
    }
  };

  const isRecurringAppointment = useCallback((appointment) => {
    if (!appointment || Object.keys(appointment).length === 0) return false;
    
    return Boolean(
      appointment?.recurringId && 
      appointment?.recurringType && 
      appointment?.recurringEndDate
    );
  }, []);

  // Use the function with React.memo to prevent unnecessary re-renders
  useEffect(() => {
    if (appointment) {
      isRecurringAppointment(appointment);
    }
  }, [appointment?.recurringId, appointment?.recurringType, appointment?.recurringEndDate, isRecurringAppointment]);

  const handleCancelRecurring = (cancelAll) => {
    setConfirmCancelDialog({ open: true, cancelAll });
  };

  const handleConfirmCancelRecurring = async () => {
    if (!appointmentData?.recurringId || !companyData?.id) return;
    
    try {
      setCancellingAppointments(true);
      const { updatedByName, updatedByUid } = getCurrentUpdaterMetadata();
      const seriesStart = toAppointmentDate(appointmentData.dateTime);
      const result = await listAppointments({
        companyId: companyData.id,
        archived: false,
      });
      const rows = result.appointments || [];
      const matches = rows.filter((row) => {
        if (String(row.recurringId || '') !== String(appointmentData.recurringId)) return false;
        const rowDate = toAppointmentDate(row.dateTime);
        if (!seriesStart || !rowDate) return true;
        return rowDate.getTime() >= seriesStart.getTime();
      });

      onLiveAppointmentWillBeDeleted?.();
      await Promise.all(
        matches.map((row) =>
          archiveCompanyAppointment(null, companyData.id, row, {
            status: 'cancelled',
            cancelReason: 'Cancelled recurring appointment',
            updatedBy: 'employee',
            updatedByName: updatedByName || null,
            updatedByUid: updatedByUid || null,
            lastAction: 'cancelled',
            archivedBy: companyData.id,
          })
        )
      );

      setConfirmCancelDialog({ open: false, cancelAll: false });
      setCancellingAppointments(false);
      onClose();

    } catch (error) {
      console.error('Error cancelling recurring appointments:', error);
      setCancellingAppointments(false);
    }
  };

  // Add handlers for QuickActions
  const handleQuickConfirm = async (appointment) => {
    try {
      const { updatedByName, updatedByUid } = getCurrentUpdaterMetadata();
      const now = new Date().toISOString();
      const updateData = {
        status: 'confirmed',
        updatedAt: now,
        updatedBy: 'employee',
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'confirmed',
        clientConfirmed: appointment.clientConfirmed || false
      };

      await upsertAppointment({
        companyId: companyData.id,
        id: appointment.id,
        appointment: updateData,
      });
      await recordStaffUpdate(appointment, 'confirmed', {
        updatedBy: 'employee',
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'confirmed',
        clientConfirmed: appointment.clientConfirmed || false
      });
      
      // Update local state
      setLocalAppointment({
        ...appointment,
        ...updateData,
        updatedAt: new Date()
      });
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error confirming appointment:', error);
    }
  };

  const handleQuickCancel = async (appointment) => {
    try {
      const { updatedByName, updatedByUid } = getCurrentUpdaterMetadata();
      const cancelReason = appointment?.cancelReason?.trim();
      if (!cancelReason) {
        showToast('Cancellation reason is required', 'error');
        throw new Error('Cancellation reason is required');
      }

      if (!companyData?.id) {
        showToast('Company not loaded', 'error');
        throw new Error('Company not loaded');
      }

      onLiveAppointmentWillBeDeleted?.();

      await archiveCompanyAppointment(null, companyData.id, appointment, {
        status: 'cancelled',
        cancelReason,
        updatedBy: 'employee',
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'cancelled',
      });

      recordStaffUpdate(appointment, 'cancelled', {
        updatedBy: 'employee',
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'cancelled',
        cancelReason,
      });

      setLocalAppointment({
        ...appointment,
        status: 'cancelled',
        cancelReason,
        updatedAt: new Date(),
        archivedAt: new Date()
      });

      if (onUpdate) onUpdate();
      onClose();
      showToast('Appointment cancelled successfully', 'success');
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      const message = error?.code === 'permission-denied'
        ? 'You do not have permission to cancel this appointment.'
        : (error?.message || 'Failed to cancel appointment. Please try again.');
      showToast(message, 'error');
    }
  };

  const handleQuickReschedule = async (appointment, newDateTime) => {
    if (!appointment || !newDateTime) {
      console.error('Missing required data for reschedule:', { appointment, newDateTime });
      return;
    }

    try {
      const { updatedByName, updatedByUid } = getCurrentUpdaterMetadata();
      const durationMinutes = Number(appointment.duration) > 0 ? Number(appointment.duration) : 60;
      const now = new Date().toISOString();
      const previousDateTime = appointment.dateTime instanceof Date
        ? appointment.dateTime.toISOString()
        : appointment.dateTime;
      const updateData = {
        dateTime: newDateTime.toISOString(),
        endTime: addMinutes(newDateTime, durationMinutes).toISOString(),
        status: 'rescheduled',
        updatedAt: now,
        updatedBy: 'employee',
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'rescheduled',
        previousDateTime,
        reminderSent: false,
        reminderSMSSent: false,
        automatedCheckInReminderSent: false,
      };

      await upsertAppointment({
        companyId: companyData.id,
        id: appointment.id,
        appointment: updateData,
      });

      try {
        await recordStaffUpdate(appointment, 'rescheduled', {
          previousDateTime: appointment.dateTime,
          newDateTime,
          updatedBy: 'employee',
          updatedByName: updatedByName || null,
          updatedByUid: updatedByUid || null,
          lastAction: 'rescheduled'
        });
      } catch (updateError) {
        console.warn('Non-blocking: failed to write updates record', updateError);
      }

      // Update local state
      setLocalAppointment({
        ...appointment,
        ...updateData,
        updatedAt: new Date()
      });

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
    }
  };

  const handleQuickNoShow = async (appointment) => {
    try {
      if (!companyData?.id) {
        showToast('Company not loaded', 'error');
        return;
      }

      onLiveAppointmentWillBeDeleted?.();

      const { updatedByName, updatedByUid } = getCurrentUpdaterMetadata();

      await archiveCompanyAppointment(null, companyData.id, appointment, {
        status: 'no show',
        updatedBy: 'employee',
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'no show',
      });

      recordStaffUpdate(appointment, 'no show', {
        updatedBy: 'employee',
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'no show',
      });

      setLocalAppointment({
        ...appointment,
        status: 'no show',
        updatedAt: new Date(),
        archivedAt: new Date()
      });
      
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      console.error('Error marking appointment as no show:', error);
    }
  };

  const handleQuickComplete = async (appointment) => {
    try {
      const now = new Date().toISOString();
      const completionData = {
        status: 'completed',
        completedAt: now,
        updatedAt: now,
        updatedBy: 'employee',
        lastAction: 'completed'
      };

      await upsertAppointment({
        companyId: companyData.id,
        id: appointment.id,
        appointment: completionData,
      });
      await recordStaffUpdate(appointment, 'completed', {
        updatedBy: 'employee',
        lastAction: 'completed'
      });
      
      // Update local state before closing
      setLocalAppointment({
        ...appointment,
        ...completionData,
        updatedAt: new Date(),
        completedAt: new Date()
      });
      
      if (onUpdate) onUpdate();
      if (variant !== 'page') onClose();
    } catch (error) {
      console.error('Error marking appointment as completed:', error);
    }
  };

  const handleMarkAsPaid = async (appointment) => {
    try {
      const now = new Date().toISOString();
      const existingPaymentDetails =
        appointment.paymentDetails && typeof appointment.paymentDetails === 'object'
          ? appointment.paymentDetails
          : {};
      const updateData = {
        paymentStatus: 'paid',
        depositStatus: 'paid',
        updatedAt: now,
        updatedBy: 'employee',
        lastAction: 'payment_confirmed',
        paymentDetails: {
          ...existingPaymentDetails,
          manualPayment: {
            status: 'paid',
            paidAt: now,
            confirmedBy: 'employee'
          }
        }
      };

      await upsertAppointment({
        companyId: companyData.id,
        id: appointment.id,
        appointment: updateData,
      });
      await recordStaffUpdate(appointment, 'payment_confirmed', {
        updatedBy: 'employee',
        lastAction: 'payment_confirmed'
      });
      
      // Update local state
      setLocalAppointment({
        ...appointment,
        ...updateData,
        updatedAt: new Date()
      });
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error marking appointment as paid:', error);
    }
  };

  const getNotes = () => {
    if (!appointmentData.notes) return '';
    
    if (typeof appointmentData.notes === 'string') {
      return appointmentData.notes.trim();
    }
    
    if (typeof appointmentData.notes === 'object' && appointmentData.notes.notes) {
      return typeof appointmentData.notes.notes === 'string' 
        ? appointmentData.notes.notes.trim() 
        : '';
    }
    
    return '';
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'info';
      case 'deposit_paid':
        return 'success';
      default:
        return 'default';
    }
  };

  const getBalanceStatusLabel = (appointmentData) => {
    if (appointmentData.paymentStatus === 'paid') {
      return 'paid';
    }
    // If deposit is paid but full payment isn't made yet, balance should be pending
    if (appointmentData.depositStatus === 'paid' || appointmentData.paymentStatus === 'deposit_paid') {
      return 'pending';
    }
    return appointmentData.paymentStatus || 'pending';
  };

  const formatStatusLabel = (statusOrAppointment, fallback = 'Pending') => {
    if (statusOrAppointment && typeof statusOrAppointment === 'object') {
      return getAppointmentDisplayMeta(statusOrAppointment).label;
    }
    if (!statusOrAppointment) return fallback;
    return String(statusOrAppointment)
      .replace(/[_-]+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const rawDepositStatus = appointmentData.depositStatus;
  const normalizedDepositStatus = String(rawDepositStatus || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  const displayDepositStatus = rawDepositStatus === 'deposit_paid' ? 'paid' : rawDepositStatus;
  const depositAmount = parseFloat(appointmentData.deposit?.total || appointmentData.deposit?.amount || 0);
  const hasDepositAmount = Number.isFinite(depositAmount) && depositAmount > 0;
  const isDepositNotRequired = normalizedDepositStatus === 'not_required' || normalizedDepositStatus === 'not_required';
  const explicitDepositRequired = appointmentData.depositRequired ?? appointmentData.requiresDeposit;
  const shouldShowDepositInfo = explicitDepositRequired === false
    ? false
    : explicitDepositRequired === true
      ? true
      : hasDepositAmount || (!!rawDepositStatus && !isDepositNotRequired);

  const canGetDirections = useMemo(() => {
    const clientAddress = clientDetails?.address || appointmentData.clientAddress || appointmentData.customerInfo?.address;
    const isMobileBusiness = Boolean(companyData?.businessInfo?.mobileBusiness);
    // Mobile staff travel to the client; company HQ may be incomplete or unused — only the client must be routable.
    return Boolean(isMobileBusiness && hasRoutableAddress(clientAddress));
  }, [clientDetails?.address, appointmentData.clientAddress, appointmentData.customerInfo?.address, companyData?.businessInfo?.mobileBusiness]);

  const handleGetDirections = () => {
    try {
      const clientAddress = clientDetails?.address || appointmentData.clientAddress || appointmentData.customerInfo?.address;
      if (!hasRoutableAddress(clientAddress)) return;
      // Mobile business: staff travel to the client — never pre-fill "from" with the company card
      // address; Maps defaults to the user's current location when origin is omitted.
      const url = buildDirectionsUrl({ origin: undefined, destination: clientAddress, travelMode: 'driving' });
      if (!url) return;
      window.open(url, '_blank');
    } catch (e) {
      console.error('Failed to open directions:', e);
    }
  };

  const normalizedAppointmentStatus = String(appointmentData.status || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, ' ');
  const normalizedRescheduleStatus = String(appointmentData.rescheduleStatus || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, ' ');
  const shouldShowRescheduleStatusChip =
    Boolean(normalizedRescheduleStatus) &&
    normalizedRescheduleStatus !== normalizedAppointmentStatus;

  const confirmCancelDialogJsx = (
    <Dialog
      open={confirmCancelDialog.open}
      onClose={() => setConfirmCancelDialog({ open: false, cancelAll: false })}
    >
      <DialogTitle>
        {confirmCancelDialog.cancelAll 
          ? "Cancel All Future Recurring Appointments?" 
          : "Cancel This Appointment?"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {confirmCancelDialog.cancelAll 
            ? "This will cancel all future appointments in this recurring series. This action cannot be undone."
            : "This will cancel only this instance of the recurring appointment. Other appointments in the series will not be affected."}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => setConfirmCancelDialog({ open: false, cancelAll: false })}
          disabled={cancellingAppointments}
        >
          No, Keep Appointments
        </Button>
        <Button 
          onClick={handleConfirmCancelRecurring}
          color="error"
          disabled={cancellingAppointments}
          autoFocus
        >
          Yes, Cancel {confirmCancelDialog.cancelAll ? "All" : "This"} Appointment{confirmCancelDialog.cancelAll ? "s" : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const mainContent = isPage ? (
    <>
      <AppointmentDetailsPageLayout
        appointmentData={appointmentData}
        clientDetails={clientDetails}
        clientId={appointmentData?.clientId}
        clientProfileLoading={clientProfileLoading}
        companyData={companyData}
        serviceDetails={getServiceDetails(appointmentData)}
        formatDateTime={formatDateTime}
        calculateEndTime={calculateEndTime}
        notes={getNotes()}
        formatStatusLabel={formatStatusLabel}
        getStatusColor={getStatusColor}
        getPaymentStatusColor={getPaymentStatusColor}
        getBalanceStatusLabel={getBalanceStatusLabel}
        shouldShowDepositInfo={shouldShowDepositInfo}
        depositAmount={depositAmount}
        displayDepositStatus={displayDepositStatus}
        shouldShowRescheduleStatusChip={shouldShowRescheduleStatusChip}
        canGetDirections={canGetDirections}
        isRecurring={isRecurringAppointment(appointmentData)}
        updatedBy={appointmentData.updatedBy}
        updatedAt={appointmentData.updatedAt}
        rescheduledAt={appointmentData.rescheduledAt}
        rescheduleHistory={appointmentData.rescheduleHistory}
        onConfirm={handleQuickConfirm}
        onCancel={handleQuickCancel}
        onReschedule={handleQuickReschedule}
        onNoShow={handleQuickNoShow}
        onMarkAsPaid={handleMarkAsPaid}
        onComplete={handleQuickComplete}
        onGetDirections={handleGetDirections}
        onClose={onClose}
        onUpdate={onUpdate}
        compactButtonStyle={compactButtonStyle}
        onMediaUpdate={async (type, file, action = 'add') => {
          try {
            const key = `${type}s`;
            const nextList = nextAppointmentMediaList(appointmentData, type, file, action);
            await upsertAppointment({
              companyId: companyData.id,
              id: appointmentData.id,
              appointment: { [key]: nextList },
            });
            setLocalAppointment({
              ...appointmentData,
              [key]: nextList,
            });
            if (onUpdate) onUpdate();
          } catch (err) {
            console.error('Error updating media:', err);
          }
        }}
      />
      {canClientSms && companyData?.id && (
        <AppointmentSmsChatPopout
          companyId={companyData.id}
          customerPhone={appointmentSmsPhone}
          clientDisplayName={smsClientDisplayName}
          clientAvatarUrl={clientDetails?.avatarUrl}
        />
      )}
      {confirmCancelDialogJsx}
    </>
  ) : (
    <>
      <Box
        component={DialogTitle}
        sx={{ 
          pb: 1,
          ...(isMobile && {
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.paper',
            zIndex: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          })
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">
              {appointmentData.clientName}
            </Typography>
          </Box>
          {!isArchived && (
            <Box display="flex" alignItems="center" gap={1}>
              <QuickActions
                appointment={appointmentData}
                onConfirm={handleQuickConfirm}
                onCancel={handleQuickCancel}
                onReschedule={handleQuickReschedule}
                onNoShow={handleQuickNoShow}
                companyId={companyData?.id}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Divider />

      <Box
        component={DialogContent}
        sx={{ 
          pt: 2,
          ...(isMobile && {
            pb: 8,
            px: 2
          })
        }}
      >
          {appointment ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ServiceDetails 
                  service={getServiceDetails(appointmentData)} 
                />
                {/* Date & Time */}
                <ListItem>
                  <ListItemIcon>
                    <EventIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <>
                        {formatDateTime(appointmentData.dateTime)}
                        {appointmentData.duration && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' '}-{' '}
                            {format(calculateEndTime(appointmentData.dateTime, appointmentData.duration), 'h:mm a')}
                          </Typography>
                        )}
                      </>
                    }
                    secondary="Date & Time"
                    primaryTypographyProps={{ variant: 'body2', component: 'div' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>

                {(appointmentData.locationName || appointmentData.locationAddress?.fullAddress) && (
                  <ListItem>
                    <ListItemIcon>
                      <LocationOnIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={appointmentData.locationName || 'Location'}
                      secondary={
                        appointmentData.locationAddress?.fullAddress
                        || appointmentData.locationAddress
                        || 'Location details unavailable'
                      }
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                )}

                {/* Status Update Info */}
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        <Typography component="span" variant="body2">Status:</Typography>
                        <Chip
                          label={formatStatusLabel(appointmentData.status, 'Unknown')}
                          size="small"
                          sx={{ 
                            bgcolor: getStatusColor(appointmentData.status),
                            color: 'white',
                            textTransform: 'capitalize',
                            height: 20,
                            '& .MuiChip-label': {
                              px: 0.5,
                              fontSize: '0.7rem'
                            }
                          }}
                        />
                        {shouldShowRescheduleStatusChip && (
                          <Chip
                            label={formatStatusLabel(appointmentData.rescheduleStatus, 'Unknown')}
                            size="small"
                            sx={{ 
                              bgcolor: theme.palette.info.main,
                              color: 'white',
                              textTransform: 'capitalize',
                              height: 20,
                              '& .MuiChip-label': {
                                px: 0.5,
                                fontSize: '0.7rem'
                              }
                            }}
                          />
                        )}
                        {appointmentData.updatedBy && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' '}(by {
                              appointmentData.updatedByName ? appointmentData.updatedByName :
                              appointmentData.updatedBy === 'client' ? 'Client' :
                              'Employee'
                            })
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        {appointmentData.updatedAt ? 
                          `Updated ${formatDateTime(appointmentData.updatedAt)}` : 
                          'No update information'
                        }
                        {appointmentData.rescheduledAt && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Rescheduled: {formatDateTime(appointmentData.rescheduledAt)}
                          </Typography>
                        )}
                      </>
                    }
                    primaryTypographyProps={{ variant: 'body2', component: 'div' }}
                    secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
                  />
                </ListItem>

                {/* Reschedule History */}
                {appointmentData.rescheduleHistory && appointmentData.rescheduleHistory.length > 0 && (
                  <ListItem>
                    <ListItemIcon>
                      <HistoryIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2">
                            Reschedule History ({appointmentData.rescheduleHistory.length})
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => setIsRescheduleHistoryExpanded((prev) => !prev)}
                            sx={{ minWidth: 'auto', p: 0.5, lineHeight: 1 }}
                          >
                            {isRescheduleHistoryExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </Button>
                        </Box>
                      }
                      secondary={
                        <Collapse in={isRescheduleHistoryExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ mt: 1 }}>
                            {appointmentData.rescheduleHistory.map((history, index) => (
                              <Box key={index} sx={{ mb: 1 }}>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  From: {formatDateTime(history.from)}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  To: {formatDateTime(history.to)}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  By: {history.by.name} - {formatDateTime(history.at)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      }
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                )}

                {/* Duration */}
                <ListItem>
                  <ListItemIcon>
                    <TimeIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={`${appointmentData.duration} minutes`}
                    secondary="Duration"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>

                {/* Payment Status */}
                <ListItem>
                  <ListItemIcon>
                    <PaymentIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography component="span" variant="body2" fontWeight="medium">
                          Payment Information
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1.5 }}>
                        {/* Payment Status Section */}
                        <Box>
                          {/* Deposit */}
                          {shouldShowDepositInfo && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                              <Box sx={{ width: '100px' }}>
                                <Typography variant="body2" color="text.secondary">Deposit:</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', minWidth: '70px' }}>
                                  ${depositAmount.toFixed(2)}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={formatStatusLabel(displayDepositStatus)}
                                  color={getPaymentStatusColor(displayDepositStatus)}
                                  sx={{ height: 24, borderRadius: 1, ml: 1 }}
                                />
                              </Box>
                            </Box>
                          )}
                          
                          {/* Balance */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ width: '100px' }}>
                              <Typography variant="body2" color="text.secondary">Balance:</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'medium', minWidth: '70px' }}>
                                ${parseFloat(appointmentData.balancePayment?.totalWithTax || appointmentData.totalPrice - parseFloat(appointmentData.deposit?.amount || 0)).toFixed(2)}
                              </Typography>
                              <Chip
                                size="small"
                                label={formatStatusLabel(getBalanceStatusLabel(appointmentData))}
                                color={getPaymentStatusColor(getBalanceStatusLabel(appointmentData))}
                                sx={{ height: 24, borderRadius: 1, ml: 1 }}
                              />
                            </Box>
                          </Box>

                          {/* Original Total */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ width: '100px' }}>
                              <Typography variant="body2" color="text.secondary">Original Total:</Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                ${parseFloat(appointmentData.totalPrice || 0).toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Tip if exists */}
                          {appointmentData.payment?.tip?.amount > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                              <Box sx={{ width: '100px' }}>
                                <Typography variant="body2" color="text.secondary">
                                  Tip ({appointmentData.payment.tip.percentage}%):
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  ${parseFloat(appointmentData.payment.tip.amount || 0).toFixed(2)}
                                </Typography>
                              </Box>
                            </Box>
                          )}

                          {/* Total with Tip */}
                          {appointmentData.payment?.tip?.amount > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ width: '100px' }}>
                                <Typography variant="body2" color="text.secondary" fontWeight="500">
                                  Total with Tip:
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  ${parseFloat(appointmentData.totalPrice + appointmentData.payment.tip.amount).toFixed(2)}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    }
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>

                {/* Contact Info */}
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={appointmentData.clientEmail || clientDetails?.email || appointmentData.customerInfo?.email || 'No email provided'}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                    }
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={appointmentData.clientPhone || clientDetails?.phone || appointmentData.customerInfo?.phone || 'No phone provided'}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                    }
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>

                {/* Additional customer info */}
                {appointmentData.customerInfo && (
                  <ListItem>
                    <ListItemIcon>
                      <PersonIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Box>
                          <Typography variant="body2">
                            {appointmentData.customerInfo.name || appointmentData.clientName}
                          </Typography>
                          {appointmentData.createdAt && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Created: {formatDateTime(appointmentData.createdAt)}
                            </Typography>
                          )}
                        </Box>
                      }
                      primaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                )}
              </Grid>

              {/* Notes */}
              {getNotes() && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ pl: 2 }}>
                    {getNotes()}
                  </Typography>
                </Grid>
              )}

              {/* Documents and Photos */}
              <Grid item xs={12}>
                <AppointmentMedia 
                  appointmentId={appointmentData.id}
                  documents={appointmentData.documents || []}
                  photos={appointmentData.photos || []}
                  onUpdate={async (type, file, action = 'add') => {
                    try {
                      const key = `${type}s`;
                      const nextList = nextAppointmentMediaList(appointmentData, type, file, action);
                      await upsertAppointment({
                        companyId: companyData.id,
                        id: appointmentData.id,
                        appointment: { [key]: nextList },
                      });
                      setLocalAppointment({
                        ...appointmentData,
                        [key]: nextList,
                      });
                      if (onUpdate) {
                        onUpdate();
                      }
                    } catch (error) {
                      console.error(`Error ${action === 'add' ? 'adding' : 'removing'} ${type}:`, error);
                    }
                  }}
                />
              </Grid>

              {isRecurringAppointment(appointmentData) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Recurring Appointment
                  </Typography>
                  <List dense disablePadding>
                    <ListItem>
                      <ListItemIcon>
                        <RepeatIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={`${appointmentData.recurringType.charAt(0).toUpperCase() + appointmentData.recurringType.slice(1)} Appointment`}
                        secondary={`Part of recurring series (ID: ${appointmentData.recurringId})`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </List>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography>No appointment details available</Typography>
          )}
      </Box>

      <Box
        component={isPage ? 'div' : DialogActions}
        sx={{
          ...(isMobile && !isPage && {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1,
            zIndex: 1
          }),
          ...(isPage && { pt: 2, pb: 0 })
        }}
      >
          <Box sx={{ 
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
            gap: 1
          }}>
            {/* Only show Mark as Paid for cash/manual bookings */}
            {!isArchived && canStaffManuallyMarkAppointmentPaid(appointmentData) && (
              <GradientButton
                onClick={() => handleMarkAsPaid(appointmentData)}
                variant="success"
                size="small"
                sx={{
                  minWidth: 'auto',
                  '&.Mui-disabled': {
                    color: 'white'
                  }
                }}
                style={compactButtonStyle}
              >
                Mark as Paid
              </GradientButton>
            )}
            {!isArchived && appointmentData?.status !== 'completed' && 
             appointmentData?.status !== 'cancelled' && 
             appointmentData?.status !== 'no show' && (
              <GradientButton
                onClick={() => handleQuickComplete(appointmentData)}
                variant="success"
                size="small"
                sx={{
                  minWidth: 'auto'
                }}
                style={compactButtonStyle}
              >
                Mark Complete
              </GradientButton>
            )}
            <GradientButton 
              onClick={onClose}
              variant="primary"
              size="small"
              sx={{ minWidth: 'auto' }}
              style={compactButtonStyle}
            >
              {isPage ? 'Back' : 'Close'}
            </GradientButton>
          </Box>
      </Box>

      {confirmCancelDialogJsx}
    </>
  );

  const wrapper = isPage ? (
    <Box sx={{ width: '100%' }}>{mainContent}</Box>
  ) : (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      keepMounted={false}
      TransitionProps={{ unmountOnExit: true }}
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          margin: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : 'calc(100% - 64px)',
          borderRadius: isMobile ? 0 : undefined
        }
      }}
    >
      {mainContent}
    </Dialog>
  );

  return (
    <>
      {showDialog && wrapper}
    </>
  );
};

export default React.memo(AppointmentDetailsDialog);